import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { env } from '../env';

const schema = z.object({
  email: z.string().email('Enter a valid email'),
});
type FormData = z.infer<typeof schema>;

export default function ForgotPassword() {
  const { t } = useTranslation('auth');
  const [sent, setSent] = useState(false);

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    const auth = getAuth();
    await sendPasswordResetEmail(auth, data.email, {
      url: `${env.VITE_APP_URL}/reset-password`,
    });
    // Always show success (don't leak whether the email exists)
    setSent(true);
  };

  return (
    <div className="min-h-screen bg-[#081208] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <h2 className="text-2xl font-semibold text-white mb-1">{t('forgotPassword.title')}</h2>
        <p className="text-white/40 text-sm mb-8">{t('forgotPassword.subtitle')}</p>

        {sent ? (
          <div className="rounded-lg bg-primary/10 border border-primary/20 px-4 py-3 text-sm text-primary/90 mb-4">
            {t('forgotPassword.success')}
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-xs text-white/50 mb-1.5 tracking-wide">
                {t('forgotPassword.email')}
              </label>
              <input
                {...register('email')}
                type="email"
                autoComplete="email"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-colors"
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-400">{errors.email.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-medium rounded-lg px-4 py-2.5 transition-colors"
            >
              {isSubmitting ? '...' : t('forgotPassword.submit')}
            </button>
          </form>
        )}

        <div className="mt-4 text-center">
          <Link to="/login" className="text-xs text-white/30 hover:text-white/60 transition-colors">
            {t('forgotPassword.backToLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
}
